import { Table } from "@ptsd/uikit";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text, View } from "tamagui";

import { api } from "~/utils/api";
import type { RouterOutputs } from "~/utils/api";
import { resolveFilter, useGeneralFilter } from "~/components/blocks/filters";

type SphereDynamics = RouterOutputs["dashboard"]["getSphereTable"][number];

const columnHelper = createColumnHelper<SphereDynamics>();

const columns = [
  columnHelper.accessor("name", {
    header: () => "Сфера",
    cell: (info) => info.renderValue(),
    footer: (info) => info.column.id,
  }),

  columnHelper.accessor("avg", {
    header: () => "Середнє значення",
    cell: (info) => info.renderValue()?.toFixed(2),
    footer: (info) => info.column.id,
  }),

  columnHelper.accessor("dynamic", {
    cell: (info) => {
      const value = info.getValue();
      return (
        <View
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Text
            fontFamily={"$subtitle"}
            fontSize={"$2"}
            lineHeight={"$2"}
            color={"$gray-primary-900"}
          >
            {value > 0 ? "+" : ""}
            {info.getValue().toFixed(2)}
          </Text>
        </View>
      );
    },
    header: () => "Динаміка",
    id: "dynamic",
  }),

  columnHelper.accessor("min", {
    header: () => "Мінімум",
    cell: (info) => info.renderValue()?.toFixed(2) ?? "0",
    footer: (info) => info.column.id,
  }),

  columnHelper.accessor("max", {
    header: () => "Максимум",
    cell: (info) => info.renderValue()?.toFixed(2) ?? "0",
    footer: (info) => info.column.id,
  }),
];

//TODO: fix type inference
type GroupBy = "week" | "month";

export const DynamicsTable = () => {
  const { filter } = useGeneralFilter();
  const { groupBy, ...rest } = filter;
  const [data] = api.dashboard.getSphereTable.useSuspenseQuery({
    filter: resolveFilter(rest),
    groupBy: (groupBy ?? "week") as GroupBy,
  });

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data) {
    return null;
  }

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  return (
    <Table
      alignCells={{ x: "center", y: "center" }}
      alignHeaderCells={{ y: "center", x: "center" }}
      cellHeight="$5.8"
      data-id="dynamics-table"
    >
      <Table.Head data-id="dynamics-table-head">
        {headerGroups.map((headerGroup, index) => {
          return (
            <Table.Row
              data-id="dynamics-table-head-row"
              rowLocation={
                index === 0
                  ? "first"
                  : index === headerGroups.length - 1
                    ? "last"
                    : "middle"
              }
              key={headerGroup.id}
              justifyContent="flex-start"
            >
              {headerGroup.headers.map((header, index) => {
                return (
                  <Table.HeaderCell
                    data-id={"dynamics-table-head-cell- " + header.id}
                    backgroundColor="white"
                    key={header.id}
                    justifyContent="flex-start"
                    borderWidth={0}
                    cellLocation={
                      index === 0
                        ? "first"
                        : index === headerGroup.headers.length - 1
                          ? "last"
                          : "middle"
                    }
                  >
                    <Text
                      fontFamily={"$subtitle"}
                      fontSize={"$2"}
                      lineHeight={"$2"}
                      fontWeight={"$5"}
                      color={"$gray-primary-900"}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </Text>
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          );
        })}
      </Table.Head>

      <Table.Body data-id="dynamics-table-body">
        {tableRows.length === 0 ? (
          <Table.Row
            data-id="dynamics-table-row"
            borderBottomRightRadius={"$1"}
            borderBottomLeftRadius={"$1"}
            backgroundColor={"white"}
            key={1}
          >
            <Table.Cell
              data-id="dynamics-table-cell"
              borderColor={"$gray-200"}
              width={"100%"}
              key={1}
              justifyContent="center"
            >
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                fontFamily={"$subtitle"}
                fontSize={"$2"}
                lineHeight={"$2"}
                color={"$gray-primary-900"}
              >
                Немає даних
              </Text>
            </Table.Cell>
          </Table.Row>
        ) : (
          tableRows?.map((row, index) => {
            return (
              <Table.Row
                data-id="dynamics-table-row"
                backgroundColor={index % 2 === 0 ? "white" : "$gray-50"}
                rowLocation={
                  index === 0
                    ? "first"
                    : index === tableRows.length - 1
                      ? "last"
                      : "middle"
                }
                key={row.id}
              >
                {row.getVisibleCells().map((cell, index) => {
                  return (
                    <Table.Cell
                      data-id={"dynamics-table-cell-" + cell.id}
                      justifyContent="flex-start"
                      cellLocation={
                        index === 0
                          ? "first"
                          : row.getVisibleCells().length - 1 === index
                            ? "last"
                            : "middle"
                      }
                      key={cell.id}
                    >
                      <Text
                        fontFamily={"$subtitle"}
                        lineHeight={"$2"}
                        fontSize={"$2"}
                        fontWeight={"$4"}
                        color={"$gray-primary-900"}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Text>
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })
        )}
      </Table.Body>
    </Table>
  );
};
